import { useState, useEffect, useRef } from 'react';
import { T, useGT } from 'gt-react';
import { CaretDown, CaretRight, CheckCircle } from '@phosphor-icons/react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { useAgentState } from '@/context/agent-state.context';
import { getFriendlyToolName } from '@/lib/workflows/toolFriendlyNames';
import { useTodoList } from './TodoListContext';
import { TodoListItem } from './TodoListItem';

export function TodoListPanel() {
  const gt = useGT();
  const { todoList } = useTodoList();
  const { activeToolCalls, isLoading } = useAgentState();
  const [isOpen, setIsOpen] = useState(true);
  const prevHadList = useRef(false);

  useEffect(() => {
    if (todoList && !prevHadList.current) {
      setIsOpen(true);
    }
    prevHadList.current = todoList !== null;
  }, [todoList]);

  if (!todoList || todoList.items.length === 0) return null;

  const doneCount = todoList.items.filter(
    (i) => i.status === 'done' || i.status === 'skipped',
  ).length;
  const totalCount = todoList.items.length;
  const allDone = doneCount === totalCount;

  const pendingToolCall = activeToolCalls.find((tc) => tc.status === 'pending');
  // While the agent works, name the running tool; once its turn ends, an item
  // still "in progress" is waiting on the user, so say that instead of
  // spinning forever.
  const pendingSubtitle = isLoading
    ? pendingToolCall
      ? (pendingToolCall.userFacingName ??
        getFriendlyToolName(pendingToolCall.toolName))
      : undefined
    : gt('Waiting on your reply', {
        context:
          'task list subtitle when an in-progress task is paused until the user answers',
      });

  return (
    <div className="flex-shrink-0 border-b">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50 transition-colors"
          >
            <span className="flex items-center gap-1 font-medium">
              {isOpen ? (
                <CaretDown className="h-3 w-3" />
              ) : (
                <CaretRight className="h-3 w-3" />
              )}
              <T context="collapsible todo list header label in chat panel">
                Tasks
              </T>
            </span>
            <span className="flex items-center gap-1 text-stone-500">
              {allDone && (
                <CheckCircle
                  className="h-3 w-3 text-emerald-500"
                  weight="fill"
                />
              )}
              {doneCount}/{totalCount}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-2 space-y-0.5">
            {todoList.items.map((item) => {
              const isFirstInProgress =
                item.status === 'in-progress' &&
                item.id ===
                  todoList.items.find((i) => i.status === 'in-progress')?.id;
              return (
                <TodoListItem
                  key={item.id}
                  item={item}
                  subtitle={isFirstInProgress ? pendingSubtitle : undefined}
                  agentActive={isLoading}
                />
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
